import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector, useDispatch } from 'react-redux';
import { listTemplatesAPI, readTemplateAPI } from '../../Redux/TemplateDucks';
import { fetchAccountsFromAPI } from '../../Redux/AccountDucks';

// Import node field components
import { MessageNodeFields } from './MessageNodeFields';
import { ButtonNodeFields } from './ButtonNodeFields';
import { ListNodeFields } from './ListNodeFields';
import { EventNodeFields } from './EventNodeFields';
import { WSPNodeFields } from './WSPNodeFields';
import { CampaignNodeFields } from './CampaignNodeFields';
import { ConditionNodeFields } from './ConditionNodeFields';
import { AgentNodeFields } from './AgentNodeFields';
import { FlowNodeFields } from './FlowNodeFields';
import { StartNodeFields } from './StartNodeFields';
import { MiddleNodeFields } from './MiddleNodeFields';
import { APICallNodeFields } from './APICallNodeFields';
import { MediaMessageNodeFields } from './MediaMessageNodeFields';
import { WspFlowNodeFields } from './WSPFlowNodeFields';
import { DocumentPdfNodeFields } from './DocumentPdfNodeFields';
import { MediaUploadNodeFields } from './MediaUploadNodeFields';
import { EnqueueHumanNodeFields } from './EnqueueHumanNodeFields';
import { StageNodeFields } from './StageNodeFields';

export function PropertiesPanel({ selectedNode, setNodes, nodes }) {
    const dispatch = useDispatch();
    const flowList = useSelector((store) => store.flow.flowList);
    const accountList = useSelector(store => store.account.accounts) || [];
    const templateList = useSelector(store => store.template.templates) || [];
    const currentTemplate = useSelector(store => store.template.currentTemplate);
    const flowConfiguration = useSelector((store) => store.flow.flowConfiguration);

    const [localNodeData, setLocalNodeData] = useState(selectedNode?.data || {});
    const [buttons, setButtons] = useState(
        localNodeData.buttons || [
            { title: '', id: 'button-1' },
            { title: '', id: 'button-2' }
        ]
    );

    const [options, setOptions] = useState(
        localNodeData.options || [
            { title: '', id: 'option-0' },
            { title: '', id: 'option-1' }
        ]
    );

    const [error, setError] = useState({});

    const handleOptionChange = (id, value, type) => {
        if (type === "list") {
            if (value.length > 24) {
                setError(prev => ({ ...prev, [id]: 'El título no puede tener más de 24 caracteres' }));
            } else {
                setError(prev => ({ ...prev, [id]: '' }));
            }
        }
        updateOption(id, value);
    };

    const validateOptions = (options, type) => {
        const newError = {};
        if (type === "list") {
            options.forEach(option => {
                if (option.title && option.title.length > 24) {
                    newError[option.id] = 'El título no puede tener más de 24 caracteres';
                }
            });
        }
        setError(newError);
    };

    const updateOption = (id, title) => {
        const updatedOptions = options.map(option =>
            option.id === id ? { ...option, title } : option
        );
        setOptions(updatedOptions);
        updateNodeData('options', updatedOptions);
    };

    const addOption = () => {
        const newOption = { title: '', id: `option-${options.length}` };
        const updatedOptions = [...options, newOption];
        setOptions(updatedOptions);
        updateNodeData('options', updatedOptions);
    };

    const deleteOption = (id) => {
        const updatedOptions = options.filter(option => option.id !== id);
        setOptions(updatedOptions);
        updateNodeData('options', updatedOptions);
    };

    const updateButton = (id, title) => {
        const updatedButtons = buttons.map(button =>
            button.id === id ? { ...button, title } : button
        );
        setButtons(updatedButtons);
        updateNodeData('buttons', updatedButtons);
    };

    const addArrayItem = (arrayKey, newItem) => {
        setLocalNodeData(prevLocalNodeData => {
            const updatedLocalNodeData = {
                ...prevLocalNodeData,
                [arrayKey]: [...(prevLocalNodeData[arrayKey] || []), newItem]
            };
            if (selectedNode && nodes) {
                const updatedNodes = nodes.map(node =>
                    node.id === selectedNode.id
                        ? { ...node, data: updatedLocalNodeData }
                        : node
                );
                setNodes(updatedNodes);
            }
            return updatedLocalNodeData;
        });
    };

    const removeArrayItem = (arrayKey, index) => {
        setLocalNodeData(prevLocalNodeData => {
            const updatedLocalNodeData = {
                ...prevLocalNodeData,
                [arrayKey]: prevLocalNodeData[arrayKey].filter((_, i) => i !== index)
            };

            if (selectedNode && nodes) {
                const updatedNodes = nodes.map(node =>
                    node.id === selectedNode.id
                        ? { ...node, data: updatedLocalNodeData }
                        : node
                );
                setNodes(updatedNodes);
            }
            return updatedLocalNodeData;
        });
    };

    const updateArrayItem = (arrayKey, index, key, value) => {
        setLocalNodeData(prevLocalNodeData => {
            const updatedLocalNodeData = {
                ...prevLocalNodeData,
                [arrayKey]: prevLocalNodeData[arrayKey].map((item, i) =>
                    i === index ? { ...item, [key]: value } : item
                )
            };

            if (selectedNode && nodes) {
                const updatedNodes = nodes.map(node =>
                    node.id === selectedNode.id
                        ? { ...node, data: updatedLocalNodeData }
                        : node
                );
                setNodes(updatedNodes);
            }
            return updatedLocalNodeData;
        });
    };

    const handleCopyNodeId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(selectedNode?.id || '');
        } catch {
            const el = document.createElement('textarea');
            el.value = selectedNode?.id || '';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        }
    }, [selectedNode]);

    const updateObjectField = (objectKey, fieldKey, value) => {
        setLocalNodeData(prevLocalNodeData => {
            const updatedLocalNodeData = {
                ...prevLocalNodeData,
                [objectKey]: {
                    ...prevLocalNodeData[objectKey],
                    [fieldKey]: value
                }
            };

            if (selectedNode && nodes) {
                const updatedNodes = nodes.map(node =>
                    node.id === selectedNode.id
                        ? { ...node, data: updatedLocalNodeData }
                        : node
                );
                setNodes(updatedNodes);
            }
            return updatedLocalNodeData;
        });
    };

    useEffect(() => {
        setLocalNodeData(selectedNode?.data || {});
    }, [selectedNode]);

    useEffect(() => {
        dispatch(fetchAccountsFromAPI());
    }, [dispatch]);

    useEffect(() => {
        if (currentTemplate && selectedNode?.type === 'campaign') {
            try {
                let parsedButtons = [];
                if (currentTemplate.options && typeof currentTemplate.options === 'string') {
                    const templateData = JSON.parse(currentTemplate.options);
                    if (templateData.buttons && Array.isArray(templateData.buttons)) {
                        parsedButtons = templateData.buttons;
                    }
                }

                if (parsedButtons.length > 0) {
                    const mappedOptions = parsedButtons.map((button, index) => ({
                        id: `option-${index}`,
                        title: button.text || ''
                    }));
                    setOptions(mappedOptions);
                    updateNodeData('options', mappedOptions);
                } else {
                    const emptyOptions = [
                        { title: '', id: 'option-0' },
                        { title: '', id: 'option-1' }
                    ];
                    setOptions(emptyOptions);
                    updateNodeData('options', emptyOptions);
                }
            } catch (error) {
                console.error('Error parsing template options:', error);
            }
        }
    }, [currentTemplate, selectedNode]);

    useEffect(() => {
        setButtons(localNodeData.buttons || [
            { title: '', id: 'button-1' },
            { title: '', id: 'button-2' }
        ]);
        setOptions(localNodeData.options || [
            { title: '', id: 'option-0' },
            { title: '', id: 'option-1' }
        ]);

        validateOptions(localNodeData.options || [], localNodeData.type);
    }, [localNodeData]);

    const updateNodeData = useCallback((key, value) => {
        setLocalNodeData(prevData => {
            const newData = { ...prevData, [key]: value };
            const updatedNodes = nodes.map((node) =>
                node.id === selectedNode.id
                    ? { ...node, data: newData }
                    : node
            );
            setNodes(updatedNodes);
            return newData;
        });
    }, [nodes, selectedNode, setNodes]);

    const renderNodeTypeFields = () => {
        const commonProps = {
            localNodeData,
            updateNodeData
        };

        switch (selectedNode.type) {
            case 'start':
                return <StartNodeFields {...commonProps} accountList={accountList} />;
            case 'middle':
                return <MiddleNodeFields {...commonProps} templateList={templateList} accountList={accountList} />;
            case 'message':
                return <MessageNodeFields {...commonProps} />;
            case 'mediamessage':
                return <MediaMessageNodeFields {...commonProps} />;
            case 'button':
                return (
                    <ButtonNodeFields
                        {...commonProps}
                        buttons={buttons}
                        updateButton={updateButton}
                    />
                );

            case 'list':
                return (
                    <ListNodeFields
                        {...commonProps}
                        options={options}
                        error={error}
                        handleOptionChange={handleOptionChange}
                        deleteOption={deleteOption}
                        addOption={addOption}
                    />
                );

            case 'event':
                return <EventNodeFields {...commonProps} />;

            case 'wsp':
                return (
                    <WSPNodeFields
                        {...commonProps}
                        addArrayItem={addArrayItem}
                        removeArrayItem={removeArrayItem}
                        updateArrayItem={updateArrayItem}
                        updateObjectField={updateObjectField}
                    />
                );

            case 'campaign':
                return (
                    <CampaignNodeFields
                        {...commonProps}
                        accountList={accountList}
                        templateList={templateList}
                        dispatch={dispatch}
                        listTemplatesAPI={listTemplatesAPI}
                        readTemplateAPI={readTemplateAPI}
                        options={options}
                        error={error}
                        handleOptionChange={handleOptionChange}
                        deleteOption={deleteOption}
                        addOption={addOption}
                    />
                );

            case 'condition':
                return <ConditionNodeFields {...commonProps} />;

            case 'agent':
                return <AgentNodeFields {...commonProps} />;

            case 'apicall':
                return <APICallNodeFields {...commonProps} />;

            case 'flow':
                return (
                    <FlowNodeFields
                        {...commonProps}
                        flowList={flowList}
                    />
                );

            case 'wsp_flow':
                return (
                    <WspFlowNodeFields
                        {...commonProps}
                        accountList={accountList}
                    />
                );

            case 'document_pdf':
                return <DocumentPdfNodeFields {...commonProps} />;

            case 'media_upload':
                return (
                    <MediaUploadNodeFields
                        {...commonProps}
                        accountList={accountList}
                    />
                );

            case 'enqueue_human':
                return <EnqueueHumanNodeFields {...commonProps} />;

            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: '100%', p: 2, overflow: 'auto' }}>
            {selectedNode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            {selectedNode.id}
                        </Typography>
                        <Tooltip title="Copiar ID">
                            <IconButton
                                aria-label="Copiar ID del nodo"
                                size="small"
                                onClick={handleCopyNodeId}
                            >
                                <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Tipo de Nodo: {selectedNode.type}
                    </Typography>

                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            Nombre
                        </Typography>
                        <TextField
                            value={localNodeData.label || ''}
                            size="small"
                            fullWidth
                            onChange={(e) => updateNodeData('label', e.target.value)}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            Descripción
                        </Typography>
                        <TextField
                            value={localNodeData.description || ''}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            onChange={(e) => updateNodeData('description', e.target.value)}
                        />
                    </Box>

                    {renderNodeTypeFields()}

                    {/* Etapa del nodo (para reportes/seguimiento) */}
                    <StageNodeFields
                        localNodeData={localNodeData}
                        updateNodeData={updateNodeData}
                        totalStages={flowConfiguration?.totalStages || 1}
                    />
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Selecciona un nodo para ver sus propiedades
                </Typography>
            )}
        </Box>
    );
}
